import React, { useEffect, useRef } from 'react';
import { Animated, View, TouchableOpacity, StyleSheet, Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { router, type Href } from 'expo-router';
import { Plus } from 'lucide-react-native';
import { useTheme } from '../../theme';
import { ALL_TABS, FIELD_MODE_TAB_NAMES, TAB_BAR_HEIGHT } from '@constants/navigationConfig';
import { useUIStore } from '../../store/uiStore';
import { useAuthStore } from '../../store/authStore';
import { useAuthGate } from '@hooks/useAuthGate';
import { TAB_ROUTE_MAP, useActiveTab } from '@hooks/useTabNavigation';
import { useWebHover } from '@hooks/useWebHover';
import { useOpenMapPathsOverlay } from '@hooks/useOpenMapPathsOverlay';
import { useOpenProperty } from '@hooks/useOpenProperty';
import { useFieldMode } from '@hooks/useFieldMode';
import { CURSOR_POINTER } from '@utils/webStyles';

export function GlobalFloatingTabBar() {
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const tabBarVisible = useUIStore((s) => s.tabBarVisible);
  const openSheet = useUIStore((s) => s.openSheet);
  const user = useAuthStore((s) => s.user);
  const { requireAuth } = useAuthGate();
  const openMapPathsOverlay = useOpenMapPathsOverlay();
  const openProperty = useOpenProperty();
  const fieldMode = useFieldMode();
  const opacity = useRef(new Animated.Value(tabBarVisible ? 1 : 0)).current;

  useEffect(() => {
    Animated.timing(opacity, {
      toValue: tabBarVisible ? 1 : 0,
      duration: 200,
      useNativeDriver: Platform.OS !== 'web',
    }).start();
  }, [tabBarVisible, opacity]);

  // Field mode (phone website) keeps Property, Community and "+" only.
  const tabs = fieldMode ? ALL_TABS.filter((t) => FIELD_MODE_TAB_NAMES.includes(t.name)) : ALL_TABS;

  // Determine which tab is active based on current route (shared with SidebarNav)
  const getIsActive = useActiveTab();

  return (
    <Animated.View
      testID="global-floating-tab-bar"
      style={[
        styles.container,
        {
          // Flat bar sits flush to the bottom edge; the home-indicator safe
          // area is padding inside it.
          height: TAB_BAR_HEIGHT + insets.bottom,
          paddingBottom: insets.bottom,
          backgroundColor: colors.tabBarBackground,
          borderTopColor: colors.tabBarBorder,
          opacity,
          pointerEvents: tabBarVisible ? 'auto' : 'none',
        },
      ]}>
      {/* Cap the icon row on tablets so the five targets don't drift apart
          across the full bar width. */}
      <View style={styles.row}>
        {tabs.map((tab) => {
          const isActive = getIsActive(tab.name);
          const Icon = tab.icon;

          if (tab.isCreate) {
            // Tapping "+" opens a small action sheet (New post / Add my
            // property) — the route was a single action before properties
            // existed as a thing the user could create.
            return <CreateTabButton key={tab.name} onPress={() => openSheet('createMenu')} />;
          }

          if (tab.overlay === 'mapPaths' || tab.action === 'openProperty') {
            // Action tabs: no route, never "active". Guidance re-summons the
            // paths overlay; Property jumps to the claimed structure or the
            // map's address search (useOpenProperty).
            return (
              <TabBarItem
                key={tab.name}
                label={tab.label}
                icon={Icon}
                isActive={false}
                isRestricted={false}
                onPress={tab.overlay === 'mapPaths' ? openMapPathsOverlay : openProperty}
              />
            );
          }

          const isRestricted = !tab.publicTab && !user;

          const handlePress = () => {
            if (isRestricted) {
              requireAuth(() => router.push(TAB_ROUTE_MAP[tab.name] as Href), {
                message: `Sign in to access ${tab.label}`,
                allowAnonymous: true,
              });
            } else {
              router.push(TAB_ROUTE_MAP[tab.name] as Href);
            }
          };

          return (
            <TabBarItem
              key={tab.name}
              label={tab.label}
              icon={Icon}
              isActive={isActive}
              isRestricted={isRestricted}
              onPress={handlePress}
            />
          );
        })}
      </View>
    </Animated.View>
  );
}

function CreateTabButton({ onPress }: { onPress: () => void }) {
  const { colors } = useTheme();
  const { hovered, hoverProps } = useWebHover();
  return (
    <TouchableOpacity
      onPress={onPress}
      {...hoverProps}
      style={[styles.createButton, CURSOR_POINTER]}
      activeOpacity={0.8}
      accessibilityRole="button"
      accessibilityLabel="Create">
      <View
        style={[
          styles.createButtonInner,
          { backgroundColor: colors.action, opacity: hovered ? 0.85 : 1 },
        ]}>
        <Plus size={18} color={colors.actionOn} strokeWidth={2.5} />
      </View>
    </TouchableOpacity>
  );
}

function TabBarItem({
  label,
  icon: Icon,
  isActive,
  isRestricted,
  onPress,
}: {
  label: string;
  icon: React.ComponentType<{ size?: number; color?: string; strokeWidth?: number }>;
  isActive: boolean;
  isRestricted: boolean;
  onPress: () => void;
}) {
  const { colors } = useTheme();
  const { hovered, hoverProps } = useWebHover();
  return (
    <TouchableOpacity
      onPress={onPress}
      {...hoverProps}
      style={[styles.tabButton, isRestricted && styles.dimmed, CURSOR_POINTER]}
      activeOpacity={0.7}
      accessibilityRole="tab"
      accessibilityLabel={label}
      accessibilityState={{ selected: isActive }}>
      {hovered && (
        <View style={[styles.activeBackground, { backgroundColor: colors.hoverSurface }]} />
      )}
      <Icon
        size={21}
        color={isActive ? colors.tabBarActive : colors.tabBarInactive}
        strokeWidth={isActive ? 2.2 : 1.6}
      />
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  // Plaud grammar: flat solid bar, edge to edge, hairline top border.
  // No radius, no shadow, no translucency (Jason's call).
  container: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    borderTopWidth: 1,
    alignItems: 'center',
    zIndex: 1000,
  },
  row: {
    flex: 1,
    width: '100%',
    maxWidth: 520,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-evenly',
  },
  tabButton: {
    width: 44,
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  createButton: {
    width: 44,
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  createButtonInner: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
  },
  activeBackground: {
    position: 'absolute' as const,
    width: 36,
    height: 36,
    borderRadius: 18,
  },
  dimmed: {
    opacity: 0.4,
  },
});
